export { storefrontShouldRevalidate as shouldRevalidate } from "./helpers/storefront-should-revalidate";

import type { AddressResult } from "@dransay/address-validation";
import { createCaller } from "@dransay/api";
import { type CurrentCustomer, cartSchema } from "@dransay/api/models";
import { getAllFeatureFlagsFlatMap } from "@dransay/feature-flags";
import { logger } from "@dransay/logger";
import * as Sentry from "@sentry/react-router";
import { TRPCError } from "@trpc/server";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	data,
	Outlet,
	redirect,
	useFetcher,
	useSearchParams,
} from "react-router";
import { DeliveryAddressModal } from "~/components/delivery-address-modal/delivery-address-modal.component";
import { FavoritesProvider } from "~/contexts/favorites.context";
import { InfiniteScrollCacheProvider } from "~/contexts/infinite-scroll-cache.context";
import { VendorSelectionProvider } from "~/contexts/vendor-selection.context";
import { DeferredOneTap } from "~/features/auth/deferred-one-tap";
import { CartProvider } from "~/features/cart/cart.context";
import {
	deleteCartCookie,
	getCartCookie,
	isCartCookieValid,
	mapCartToCartCookie,
	serializeCartCookie,
} from "~/features/cart/cart.cookie";
import { OpenCartFromNavigation } from "~/features/cart/open-cart-from-navigation.component";
import { APPLY_DELIVERY_ADDRESS_EVENT } from "~/features/delivery-address/apply-delivery-address";
import {
	buildDisplayString,
	type DeliveryAddressCookieData,
	deleteDeliveryAddressCookie,
	parseDeliveryAddressCookie,
	serializeDeliveryAddressCookie,
	shouldHydrateDeliveryAddressFromCustomerRecord,
} from "~/features/delivery-address/delivery-address.cookie";
import {
	deleteDeliveryAddressDismissedCookie,
	isDeliveryAddressModalDismissed,
} from "~/features/delivery-address/delivery-address-dismissed.cookie";
import { OPEN_DELIVERY_ADDRESS_MODAL_EVENT } from "~/features/delivery-address/delivery-address-modal.events";
import { withoutCoordinates } from "~/features/delivery-address/without-coordinates";
import { PlpExpressAvailabilityModalHost } from "~/features/products/plp-express-availability-modal-host.component";
import { StorefrontHeader } from "~/features/products/storefront-header.component";
import { StorefrontHeaderMobile } from "~/features/products/storefront-header-mobile.component";
import { ReorderPartialFromNavigation } from "~/features/profile/profile.reorder-partial-from-navigation.component";
import { useAddressSuggestions } from "~/hooks/use-address-suggestions.hook";
import { useAuthTelemetryEvent } from "~/hooks/use-auth-telemetry-event.hook";
import { useCurrentLocation } from "~/hooks/use-current-location.hook";
import { useDeliveryAddressSync } from "~/hooks/use-delivery-address-sync";
import { useIosNavigationLock } from "~/hooks/use-ios-navigation-lock.hook";
import { useUserIdentifiedEvent } from "~/hooks/use-user-identified-event.hook";
import { getAppConfig } from "~/tooling/app.config";
import { getRequestUser } from "~/tooling/authentication/request-user-store";
import { getSsoClient } from "~/tooling/authentication/sso-client";
import { getExpressPilotVendorIds } from "~/tooling/express-pilot-pharmacies.server";
import { RouteHistogram } from "~/tooling/metrics.server";
import { cookieSessionStorage, sessionUserSchema } from "~/tooling/session";
import {
	consumePendingAuthTelemetryFromSession,
	type PendingAuthTelemetry,
} from "~/tooling/session/consume-pending-auth-telemetry";
import { utmSessionStorage } from "~/tooling/telemetry/utm.cookie";
import {
	readUtmTagsFromUrl,
	writeUtmTagsToSession,
} from "~/tooling/telemetry/utm-tags";
import { VENDOR_PARAMS } from "~/utils/vendor";

const FooterV2 = lazy(() =>
	import("../components/footer-v2/footer-v2.component").then((m) => ({
		default: m.FooterV2,
	})),
);

import { OutageBanner } from "../components/outage-banner.component";
import { useError } from "../hooks/use-error.hook";
import { getContext } from "../tooling/trpc/get-context";
import type { Route } from "./+types/_storefront";
import {
	enrichCartWithVersion,
	resolveStorefrontCartVersion,
} from "./helpers/enrich-cart-with-version";

/**
 * Handle the stale-session recovery path: the authenticated user has no
 * person record in the DB, or the API refused it as a deleted account
 * (PERSON_DELETED). Clear both `_session` and `_sso_session`
 * cookies (on the configured domain AND host-only for pre-migration
 * variants), then redirect outside the `_storefront` layout tree so
 * this loader cannot re-trigger.
 *
 * Always throws — either a redirect or the original error if it isn't
 * one of those two cases.
 */
async function handleStaleSession(
	error: unknown,
	userId: string,
	url: URL,
	session: Awaited<ReturnType<typeof cookieSessionStorage.getSession>>,
	cookieHeader: string | null,
	updatedHeaders: Headers,
): Promise<never> {
	const isMissingPerson =
		error instanceof TRPCError && error.code === "NOT_FOUND";
	const isDeletedPerson =
		error instanceof TRPCError &&
		error.code === "UNAUTHORIZED" &&
		error.message === "PERSON_DELETED";

	if (!isMissingPerson && !isDeletedPerson) throw error;

	logger.warn(
		isDeletedPerson
			? "Account was deleted, destroying session"
			: "Customer record not found, destroying session",
		{ userId, error },
	);

	const { cookieSsoSessionStorage } = await import(
		"../tooling/authentication/sso-cookies"
	);
	const ssoSession = await cookieSsoSessionStorage.getSession(cookieHeader);

	updatedHeaders.append(
		"Set-Cookie",
		await cookieSessionStorage.destroySession(session),
	);
	updatedHeaders.append(
		"Set-Cookie",
		await cookieSsoSessionStorage.destroySession(ssoSession),
	);
	updatedHeaders.append(
		"Set-Cookie",
		await cookieSessionStorage.destroySession(session, {
			domain: undefined,
		}),
	);
	updatedHeaders.append(
		"Set-Cookie",
		await cookieSsoSessionStorage.destroySession(ssoSession, {
			domain: undefined,
		}),
	);
	await deleteDeliveryAddressCookie(updatedHeaders);
	await deleteDeliveryAddressDismissedCookie(updatedHeaders);

	// Redirect to "/" first to collect clearing cookies on the root
	// path; when already at "/", escape to /auth/login (outside the
	// _storefront tree) so this loader cannot re-trigger.
	const target = url.pathname !== "/" ? "/" : "/auth/login";
	throw redirect(target, { headers: updatedHeaders });
}

export const loader = async ({ request }: { request: Request }) => {
	const histogram = new RouteHistogram("storefront");
	const url = new URL(request.url);
	const cookieHeader = request.headers.get("cookie");

	let updatedHeaders = request.headers;

	const context = await getContext({ request });
	const caller = createCaller(context);

	const session = await cookieSessionStorage.getSession(cookieHeader);
	const shouldSyncFavoritesAfterLogin =
		session.get("shouldSyncFavoritesAfterLogin") === true;
	// Prefer the request-scoped user set by route guards (handles the recovery
	// path where _sso_session is valid but _session is missing/corrupted).
	// Fall back to _session cookie for normal requests.
	const requestUser = getRequestUser(request);
	const { data: sessionUser } = await sessionUserSchema.safeParseAsync(
		session.get("user"),
	);
	const user = requestUser ?? sessionUser;

	// Only fetch customer data if user is a customer.
	// Non-customers can browse the storefront; child routes enforce their own auth.
	// This avoids Single Fetch serialization errors (JUPITER-WEB-5HR) from throwing
	// responses in parent layouts.
	let customer: CurrentCustomer | null = null;
	if (user?.role === "customer") {
		try {
			customer = await caller.customer.getCurrentCustomer();
		} catch (error) {
			await handleStaleSession(
				error,
				String(user.id),
				url,
				session,
				cookieHeader,
				updatedHeaders,
			);
		}
	}

	let deliveryAddress = await parseDeliveryAddressCookie(request);
	const addressModalDismissed = await isDeliveryAddressModalDismissed(request);

	if (customer?.postalCode && customer?.city) {
		const dbAddress: DeliveryAddressCookieData = {
			postalCode: customer.postalCode,
			city: customer.city,
			addressLineOne: customer.addressLineOne ?? null,
			country: customer.countryShort ?? null,
			displayString: buildDisplayString({
				addressLineOne: customer.addressLineOne,
				postalCode: customer.postalCode,
				city: customer.city,
			}),
			source: "database",
			coordinates: customer.coordinates ?? undefined,
		};

		const isSameText =
			deliveryAddress?.postalCode === dbAddress.postalCode &&
			deliveryAddress?.city === dbAddress.city &&
			deliveryAddress?.addressLineOne === dbAddress.addressLineOne;

		// A database row without a point must never overwrite one the cookie
		// already holds: the cookie's can come from a HERE result id, which the
		// text geocode behind the stored point cannot always reproduce. When the
		// database does hold a point it stays authoritative, per FR-017.
		const dbHasDifferentPoint =
			dbAddress.coordinates !== undefined &&
			(deliveryAddress?.coordinates?.lat !== dbAddress.coordinates.lat ||
				deliveryAddress?.coordinates?.lng !== dbAddress.coordinates.lng);

		const isSameAddress = isSameText && !dbHasDifferentPoint;

		if (
			!isSameAddress &&
			shouldHydrateDeliveryAddressFromCustomerRecord(deliveryAddress)
		) {
			deliveryAddress = dbAddress;
			updatedHeaders.append(
				"Set-Cookie",
				await serializeDeliveryAddressCookie(deliveryAddress),
			);
		}
	}

	const cart = await caller.public.getCart();
	// re-setting the cookie with the db cart data will make it valid again
	// (signed in only; an anonymous cookie is rewritten by the next mutation)
	const cartCookieValid =
		(await isCartCookieValid(request)) ||
		(await cartSchema.safeParseAsync(cart)).success;
	if (!cartCookieValid) {
		logger.debug("Deleting cart cookie because it is invalid", { cart });
		updatedHeaders = await deleteCartCookie(updatedHeaders);
	}

	const cartCookie = await getCartCookie();
	const parsedCartCookie = await cartCookie.parse(cookieHeader);
	const cartVersion = resolveStorefrontCartVersion(cart, parsedCartCookie);

	const [
		stockValidationFlagEnabled,
		enableOutageBanner,
		enableProfileProductReviewsHub,
		enableTopPharmacyCartPromotion,
		enablePharmacyFilters,
		enablePlpExpressAvailabilityModal,
		enableGuestExpressCourierAvailability,
	] = await getAllFeatureFlagsFlatMap(
		[
			"stockValidation",
			"outageBanner",
			"enableProfileProductReviewsHub",
			"enableTopPharmacyCartPromotion",
			"enablePharmacyFilters",
			"enablePlpExpressAvailabilityModal",
			"enableGuestExpressCourierAvailability",
		],
		user?.emailAddress,
		context.featureFlagCache,
	);

	let ssoSession: Awaited<
		ReturnType<Awaited<ReturnType<typeof getSsoClient>>["getSession"]>
	>["ssoSession"] = null;
	let ssoClientId = "";
	let ssoUrl = "";
	try {
		const appConfig = await getAppConfig();
		ssoClientId = appConfig.SSO_CLIENT_ID ?? "";
		ssoUrl = appConfig.SSO_URL ?? "";
	} catch (error) {
		logger.error(
			{ error: error instanceof Error ? error.message : String(error) },
			"App config fetch failed — One Tap disabled",
		);
		Sentry.captureException(error, {
			level: "error",
			extra: { context: "storefront-app-config" },
		});
	}
	try {
		const ssoClient = await getSsoClient();
		const sessionData = await ssoClient.getSession(request);
		ssoSession = sessionData.ssoSession;
	} catch (error) {
		logger.error(
			{ error: error instanceof Error ? error.message : String(error) },
			"SSO bootstrap failed — storefront will render without One Tap",
		);
		Sentry.captureException(error, {
			level: "warning",
			extra: { context: "storefront-sso-bootstrap" },
		});
	}

	const utmSession = await utmSessionStorage.getSession(cookieHeader);
	const utmTags = readUtmTagsFromUrl(url);
	writeUtmTagsToSession(utmSession, utmTags);

	updatedHeaders.append(
		"Set-Cookie",
		await utmSessionStorage.commitSession(utmSession),
	);

	const versionedCart = enrichCartWithVersion(cart, cartVersion);
	// Signed in: mirror the database cart into the cookie so the basket
	// survives a logout. Anonymous: never write the cookie back. It is the only
	// store of that basket, and this loader only knows the cookie its own
	// request carried, so a response computed from an older cookie that lands
	// after an add-to-cart response would re-set the cookie to the stale basket
	// (MKP-2247). Anonymous cart writes go through the /api/cart mutations;
	// addCartItem also drops a cookie whose pharmacy no longer resolves.
	if (user) {
		updatedHeaders.append(
			"Set-Cookie",
			await serializeCartCookie(mapCartToCartCookie(versionedCart)),
		);
	}

	histogram.end(200);

	let pendingAuthTelemetry: PendingAuthTelemetry | null = null;
	let authTelemetryId: string | null = null;
	if (shouldSyncFavoritesAfterLogin) {
		session.unset("shouldSyncFavoritesAfterLogin");
	}
	const consumedAuthTelemetry = consumePendingAuthTelemetryFromSession(session);
	pendingAuthTelemetry = consumedAuthTelemetry.pendingAuthTelemetry;
	authTelemetryId = consumedAuthTelemetry.authTelemetryId;
	if (shouldSyncFavoritesAfterLogin || pendingAuthTelemetry) {
		updatedHeaders.append(
			"Set-Cookie",
			await cookieSessionStorage.commitSession(session),
		);
	}

	const expressPilotVendorIds = await getExpressPilotVendorIds();

	return data(
		{
			cart: versionedCart,
			user,
			customer: customer && withoutCoordinates(customer),
			deliveryAddress: deliveryAddress
				? withoutCoordinates(deliveryAddress)
				: null,
			addressModalDismissed,
			cartCookieValid,
			shouldSyncFavoritesAfterLogin,
			pendingAuthTelemetry,
			authTelemetryId,
			stockValidationFlagEnabled,
			enableOutageBanner,
			enableProfileProductReviewsHub,
			enableTopPharmacyCartPromotion,
			enablePharmacyFilters,
			enablePlpExpressAvailabilityModal,
			enableGuestExpressCourierAvailability,
			expressPilotVendorIds,
			ssoSession,
			ssoClientId,
			ssoUrl,
		},
		{ headers: updatedHeaders },
	);
};

export default function StorefrontLayout({ loaderData }: Route.ComponentProps) {
	const {
		cart,
		user,
		customer,
		shouldSyncFavoritesAfterLogin,
		pendingAuthTelemetry,
		authTelemetryId,
		enableOutageBanner,
		deliveryAddress,
		addressModalDismissed,
		enablePlpExpressAvailabilityModal,
		enableGuestExpressCourierAvailability,
		expressPilotVendorIds,
		ssoSession,
		ssoClientId,
		ssoUrl,
	} = loaderData;
	const { showError, hideError } = useError();
	const { t } = useTranslation(["common"]);

	// The header/PLP-modal express courier surfaces are offered to
	// authenticated customers unconditionally, and to guests only when the
	// rollout flag AND the guest-specific kill switch are both on -- so
	// support can cut guest access alone (e.g. on a cost/abuse complaint)
	// without touching the customer-facing rollout.
	const canShowExpressCourierAvailability =
		enablePlpExpressAvailabilityModal &&
		(!user || user.role === "customer") &&
		(!!user || enableGuestExpressCourierAvailability);

	// `useOneTap` is invoked from <DeferredOneTap /> below the rest of the
	// layout so the GSI client script (and the stylesheet it injects ~2.5s
	// into the page) doesn't fire during the cold-load critical path. When
	// the script's stylesheet lands during initial render, the browser
	// re-styles the document and re-decodes images against the new layer,
	// which read as a "blink" on the PDP after first load.

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		if (!loaderData.cartCookieValid) {
			showError({
				title: t("errors.cartExpired", { ns: "cart" }),
				message: t("errors.cartExpired.description", { ns: "cart" }),
				telemetry: { category: "notice", codes: ["CART_RESET"] },
				primaryButton: {
					action: hideError,
					label: t("close", { ns: "common" }),
				},
			});
		}
	}, []);

	useDeliveryAddressSync(deliveryAddress);
	useAuthTelemetryEvent(pendingAuthTelemetry, authTelemetryId);
	useUserIdentifiedEvent(user, customer);

	const {
		suggestions: addressSuggestions,
		search: searchAddress,
		isLoading: isAddressSearchLoading,
		clearResults: clearAddressSuggestions,
	} = useAddressSuggestions();

	const addressFetcher = useFetcher();
	const dismissalFetcher = useFetcher();
	const [, setSearchParams] = useSearchParams();

	const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] = useState(
		!deliveryAddress && !addressModalDismissed,
	);

	// Allow descendants (e.g. the courier/pickup map "Enter the address" CTA) to
	// open this modal without prop-drilling, via a window event.
	useEffect(() => {
		const open = () => setDeliveryAddressModalOpen(true);
		window.addEventListener(OPEN_DELIVERY_ADDRESS_MODAL_EVENT, open);
		return () =>
			window.removeEventListener(OPEN_DELIVERY_ADDRESS_MODAL_EVENT, open);
	}, []);

	useIosNavigationLock(deliveryAddressModalOpen);

	const [selectedDeliveryAddress, setSelectedDeliveryAddress] =
		useState<DeliveryAddressCookieData | null>(deliveryAddress);

	useEffect(() => {
		const apply = (event: Event) => {
			const address = (event as CustomEvent<DeliveryAddressCookieData>).detail;
			if (address) {
				setSelectedDeliveryAddress(address);
			}
		};
		window.addEventListener(APPLY_DELIVERY_ADDRESS_EVENT, apply);
		return () =>
			window.removeEventListener(APPLY_DELIVERY_ADDRESS_EVENT, apply);
	}, []);

	const handleAddressSave = (address: AddressResult) => {
		const postalCode = address.postalCode ?? "";
		const city = address.city ?? "";
		const addressLineOne = [address.street, address.houseNumber]
			.filter(Boolean)
			.join(" ");
		const location = [postalCode, city].filter(Boolean).join(" ");
		const displayString = [addressLineOne, location].filter(Boolean).join(", ");

		const cookieData: DeliveryAddressCookieData = {
			postalCode,
			city,
			addressLineOne: addressLineOne || null,
			country: address.countryCode ?? null,
			displayString: displayString.trim(),
			source: "here-api",
		};

		setSelectedDeliveryAddress(cookieData);
		setDeliveryAddressModalOpen(false);
		// Clear suggestions so a pre-filled reopen of the modal does not surface
		// stale results (the "use current location" path bypasses onClose).
		clearAddressSuggestions();

		if (postalCode) {
			setSearchParams(
				(prev) => {
					prev.set(VENDOR_PARAMS.postalCode, postalCode);
					return prev;
				},
				{ replace: true },
			);
		}

		// The HERE result id travels instead of coordinates: the server resolves
		// the point from it, so a forged location in this body cannot reach the
		// signed cookie. The optimistic local state above stays coordinate-free
		// until the server answers.
		addressFetcher.submit(
			{ ...cookieData, ...(address.id ? { hereId: address.id } : {}) },
			{
				method: "POST",
				action: "/api/delivery-address/save",
				encType: "application/json",
			},
		);
	};

	const { requestLocation, isLocating: isLocatingAddress } =
		useCurrentLocation(handleAddressSave);

	return (
		<div
			className="bg-gray-100 min-h-screen flex flex-col"
			data-storefront-scroll
		>
			<CartProvider cart={cart}>
				<OpenCartFromNavigation />
				<ReorderPartialFromNavigation />
				<FavoritesProvider
					user={user}
					shouldSyncFavoritesAfterLogin={shouldSyncFavoritesAfterLogin}
				>
					{enableOutageBanner && <OutageBanner />}
					<VendorSelectionProvider
						initialVendor={undefined}
						initialVendorData={null}
					>
						<StorefrontHeader
							user={user}
							logoVariant="default"
							deliveryAddress={selectedDeliveryAddress}
							onOpenAddressModal={() => {
								setDeliveryAddressModalOpen(true);
							}}
							expressPilotVendorIds={expressPilotVendorIds}
							enablePlpExpressAvailabilityModal={
								canShowExpressCourierAvailability
							}
						/>
						<StorefrontHeaderMobile
							user={user}
							logoVariant="default"
							deliveryAddress={selectedDeliveryAddress}
							onOpenAddressModal={() => {
								setDeliveryAddressModalOpen(true);
							}}
							expressPilotVendorIds={expressPilotVendorIds}
							enablePlpExpressAvailabilityModal={
								canShowExpressCourierAvailability
							}
						/>
						<InfiniteScrollCacheProvider>
							<div className="container mx-auto px-4 flex flex-col grow pb-0 md:pb-20">
								<Outlet />
							</div>
							{!user && ssoSession && ssoClientId && ssoUrl && (
								<DeferredOneTap
									clientId={ssoClientId}
									ssoUrl={ssoUrl}
									ssoSession={ssoSession}
								/>
							)}
						</InfiniteScrollCacheProvider>
						<DeliveryAddressModal
							open={deliveryAddressModalOpen}
							onClose={() => {
								setDeliveryAddressModalOpen(false);
								clearAddressSuggestions();
								dismissalFetcher.submit(null, {
									method: "POST",
									action: "/api/delivery-address-dismissed",
								});
							}}
							currentAddress={selectedDeliveryAddress}
							onSave={handleAddressSave}
							suggestions={addressSuggestions}
							onSearch={searchAddress}
							isLoading={isAddressSearchLoading}
							onRequestLocation={requestLocation}
							isLocating={isLocatingAddress}
							isAuthenticated={!!user}
						/>
						<PlpExpressAvailabilityModalHost
							enabled={canShowExpressCourierAvailability}
							postalCode={selectedDeliveryAddress?.postalCode}
							expressVendorIds={expressPilotVendorIds}
						/>
					</VendorSelectionProvider>
				</FavoritesProvider>
			</CartProvider>
			<Suspense fallback={null}>
				<FooterV2 />
			</Suspense>
		</div>
	);
}
